import { useState } from "react";
import type {
  CheckBoxEventValue,
  InterstateOnChangeEvent
} from "@dealer-center/core-components";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CheckBox,
  Typography
} from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import type { OperatorAuthority } from "@dealer-center/ddc-pages-configuration-interface";
import { SCREEN_READER_ONLY } from "../StatusRegion/screenReaderOnly";
import type { PermissionGroup as PermissionGroupModel } from "./groupPermissions";

export interface PermissionGroupProps {
  group: PermissionGroupModel;
  /**
   * The **operator's** authority, forwarded from the tab. **Absent means read-only** — see
   * `FeatureAccessTabProps.authority` for why that default is load-bearing.
   *
   * Only ever `OperatorAuthority`, never the subject's `FeatureAccessResponse`: SC-016 requires the
   * conflation to be a build-time error, and the type is the guard.
   */
  authority?: OperatorAuthority;
  // The Administrator entry's current (possibly unsaved) state; when checked it covers every row.
  isAdministratorChecked?: boolean;
}

/**
 * One group of permission rows: a heading, and its rows as checked/unchecked controls
 * (FR-032, FR-033, FR-046).
 *
 * **Granted state is not colour-only** (FR-050). Each row is a real `CheckBox` with `checked`, so
 * the state is a programmatic property a screen reader announces — not a class name or a tick glyph.
 * Where a row is **not** operable, `disabled` (rather than a no-op handler) is what conveys that to
 * assistive technology, which FR-052's "MUST NOT masquerade as a working one" requires.
 *
 * **Rendered as an `Accordion` so the group is collapsible and conveys its expanded state** (T034).
 *
 * **Expanded by default.** SC-007 asks for "a complete, legible, navigable list — no truncation",
 * and MUI *unmounts* a collapsed panel's children, so collapsed-by-default would put most rows
 * outside the DOM entirely — invisible to find-in-page and to a screen reader alike. Less pressing
 * now that the BFF returns only grouped rows (61-69 per account, not 186), but still the right
 * default.
 *
 * **Deliberately not wrapped in Interstate's `AccordionGroup`.** That component coordinates
 * expansion by `cloneElement`-ing its **direct** children with an injected `expanded` /
 * `defaultExpanded` prop. A wrapper component in between — this one — receives those props and does
 * not forward them, so every group silently rendered collapsed. Since each group here expands
 * independently anyway, `defaultExpanded` on the `Accordion` is both simpler and correct.
 *
 * The heading is MUI's own: `Accordion` wraps its summary in an `<h3 class="MuiAccordion-heading">`,
 * giving the `<h3><button aria-expanded>` nesting the ARIA disclosure pattern calls for and the real
 * heading element T034 asks for. Adding a second `Typography tag="h3"` inside would emit two
 * headings per group, so the label renders as a plain `span`.
 */
export const PermissionGroup = ({
  group,
  authority,
  isAdministratorChecked = false
}: PermissionGroupProps) => {
  /*
   * Toggles made since this group mounted, keyed by permission code — **overrides**, not a copy of
   * the group.
   *
   * ## Why there is local state at all, and why it persists nothing
   *
   * A row a reviewer can click has to move, or it reads as broken — and a controlled `CheckBox` whose
   * `checked` comes straight from `row.isGranted` cannot move. **Nothing is saved**: FR-083 still
   * holds, there is no write endpoint for feature access, and the header Save writes the User
   * Information fields only, never these toggles. So a toggle here is a local, deliberately
   * throwaway state.
   *
   * ⚠️ **It is discarded by the tab switch for free, but the USER switch depends on the caller.**
   * This map is keyed by permission code and **never by user**, so nothing in this component can tell
   * one subject from the next. The tab half is free — the shells hand an inactive tab an empty
   * fragment, so this unmounts. The user half is **not**: `FeatureAccessTab` keys its groups by
   * `group.key`, which is stable across users, so these instances survive a selection change unless
   * the caller remounts the tab. `UserManager` does that with `key={subject.userId}`; without it,
   * `toggledByCode[code] ?? row.isGranted` painted one user's toggles over another's real grants
   * (found in review 2026-09-22). **Any new caller that can change the subject must key the tab too.**
   *
   * It is **not** wired into `usePendingEditsGuard` — that guard compares `useUserInfoEdits`'s
   * overrides against the fetched profile and speaks only for the User Information tab (FR-023 /
   * FR-024). Feeding permission toggles into it would make FR-017's filter/sort/page changes start
   * prompting, and would claim edits are recoverable when there is nowhere to commit them. When the
   * write endpoint lands, this state is what a dirty check reads — that is the point of holding it
   * as an override map rather than mutating a copy of the rows.
   *
   * Overrides rather than a seeded copy so a refetch cannot leave stale rows on screen: an unknown
   * code falls back to the freshly-fetched `row.isGranted`.
   */
  const [toggledByCode, setToggledByCode] = useState<Record<string, boolean>>(
    {}
  );

  const handleToggle =
    (code: string) => (event: InterstateOnChangeEvent<CheckBoxEventValue>) => {
      const isChecked = Boolean(event.target?.value?.checked);
      setToggledByCode(previous => ({ ...previous, [code]: isChecked }));
    };

  return (
    <Accordion defaultExpanded data-testid={`permission-group-${group.key}`}>
      <AccordionSummary data-testid={`permission-group-summary-${group.key}`}>
        <Box
          sx={{ alignItems: "baseline", columnGap: "10px", display: "flex" }}
        >
          <Typography tag={"span"} variant={"strong-md"}>
            {group.label}
          </Typography>
          {/*
           * The row count, per the prototype's `.count`. Decorative, hence `aria-hidden`: the rows
           * are a list of labelled controls a screen reader already enumerates, so spoken aloud this
           * is a bare number ahead of the thing it counts. Its value is to a sighted user scanning
           * collapsed groups for the big one — Inventory, at 17-24 rows on the two QA accounts.
           *
           * Not a label key: a numeral is data, like the permission names beside it, not copy.
           */}
          <Typography
            aria-hidden={"true"}
            color={"sem.color.on-surface.muted"}
            data-testid={`permission-group-count-${group.key}`}
            tag={"span"}
            variant={"body-xs"}
          >
            {group.rows.length}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        {/*
         * One column, not two. A permission is a checkbox with a prose label of unpredictable length
         * — from "Powermail" to a raw `SmartSites.PageBuilder` code — so a two-column grid gives
         * every row the width of the longest label in the group and leaves ragged gaps beside the
         * short ones. Reading order also stops being obvious: with two columns the eye has to decide
         * whether the list runs across or down. The prototype's single column is the right call for a
         * scannable list of on/off rows.
         */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            rowGap: "4px"
          }}
        >
          {group.rows.map(row => {
            /*
             * FR-046. A permission the **operator** does not hold is **not grantable**, and the
             * reason is conveyed **on the row** — before any save, never as a rejected save
             * (Edge Cases). This is the prototype's own stated rule, and SC-004 makes it absolute:
             * zero paths by which an operator can grant a permission they do not themselves hold.
             *
             * Read from `authority.grantedPermissionCodes` — the **operator's** granted set — and
             * never from `row.isGranted`, which describes the **subject** and is display data
             * (FR-054). Conflating the two is the mistake SC-016 makes a build-time error.
             *
             * With `authority` absent the tab is read-only, so there is no grantability question to
             * answer and no reason to render.
             */
            const isNotGrantable = Boolean(
              authority && !authority.grantedPermissionCodes.has(row.code)
            );

            /*
             * **`!authority` is load-bearing — do NOT reduce this to `disabled={isNotGrantable}`.**
             *
             * With no `authority`, `isNotGrantable` is `false` for every row, so the bare form would
             * make the tab **editable** exactly where it must be read-only: My Info, which passes no
             * authority at all, and User Manager on the operator's own record, where the shell
             * withholds it to satisfy FR-009 / FR-055. Read-only is the absent-value default the
             * prop's own contract promises; this is where that promise is kept.
             *
             * With an authority present the row follows FR-046: operable when the operator holds the
             * permission, disabled when they do not. Unmasked on 2026-09-17 (reviewer request — the
             * blanket disable meant the real rule could never be exercised), which narrows FR-083 the
             * same way the User Information fields were narrowed on 2026-08-31: the missing endpoint
             * disables the **commit**, not the interaction.
             */
            // Only when editable; read-only rows are already disabled and explained by `ReadOnlyNotice`.
            const isCoveredByAdministrator = Boolean(
              authority && isAdministratorChecked
            );
            const isRowDisabled =
              !authority || isNotGrantable || isCoveredByAdministrator;
            // Not forced on while covered by Administrator: the row still shows the subject's own grant.
            const isGranted = toggledByCode[row.code] ?? row.isGranted;

            return (
              <Box key={row.code} sx={{ position: "relative" }}>
                <CheckBox
                  checked={isGranted}
                  data-testid={`permission-row-${row.code}`}
                  disabled={isRowDisabled}
                  id={`permission-${row.code}`}
                  label={row.label}
                  name={row.code}
                  value={row.code}
                  onChange={handleToggle(row.code)}
                />

                {/*
                 * FR-057 / SC-014: a disabled control must carry its reason, not merely be inert.
                 * Keyed on the two editable-tab causes, which are exactly the set of rows disabled *while
                 * the tab is editable* — so the reason accompanies every row a screen-reader user
                 * finds disabled and can act on, and never appears on the read-only tab, where
                 * `ReadOnlyNotice` states the one reason that applies to all of them.
                 */}
                {(isCoveredByAdministrator || isNotGrantable) && (
                  <Box
                    data-testid={`permission-row-reason-${row.code}`}
                    sx={SCREEN_READER_ONLY}
                  >
                    {
                      Locale.getText(
                        isCoveredByAdministrator
                          ? "UM_PERMISSION_INCLUDED_IN_ADMINISTRATOR"
                          : "UM_PERMISSION_NOT_GRANTABLE"
                      ) as string
                    }
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
